/**
 * Interview Scoring Panel Component
 * Coláiste Lurgan Management System - Score student interviews
 *
 * Allows ADMIN, OFFICE, and INTERVIEWER roles to score students 1-5 with notes.
 * Scores are locked to the original scorer (ADMIN can override).
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { useAuth, useUserPermissions } from '../../hooks/useAuth';
import { useScoreInterview } from '../../hooks/useStudents';
import {
  ClipboardCheck,
  Edit,
  Save,
  X,
  Loader2,
  Lock,
  User,
  Calendar
} from 'lucide-react';

const SCORE_OPTIONS = [1, 2, 3, 4, 5];
const MAX_NOTES_LENGTH = 140;

export function InterviewScoringPanel({ student, onScoreUpdated, readOnly = false }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { userType, isAdmin } = useUserPermissions();

  const [isEditing, setIsEditing] = useState(false);
  const [score, setScore] = useState(null);
  const [notes, setNotes] = useState('');

  const scoreInterviewMutation = useScoreInterview();

  // Check if user can score interviews
  const canScore = ['ADMIN', 'OFFICE', 'INTERVIEWER'].includes(userType);

  // Check if user can edit (no scorer yet, original scorer, or admin)
  const canEdit = isAdmin ||
    !student?.interview_scored_by ||
    student?.interview_scored_by === user?.id;

  // Check if score is locked (scored by someone else and user is not admin)
  const isLocked = student?.interview_scored_by &&
    student?.interview_scored_by !== user?.id &&
    !isAdmin;

  // Reset form when student changes
  useEffect(() => {
    if (student) {
      setScore(student.interview_score ?? null);
      setNotes(student.interview_notes || '');
      setIsEditing(false);
    }
  }, [student?.uuid, student?.interview_score, student?.interview_notes]);

  const handleSave = async () => {
    if (score === null) {
      toast({
        title: t('interview.error.scoreRequired'),
        variant: 'destructive',
      });
      return;
    }

    try {
      await scoreInterviewMutation.mutateAsync({
        studentUuid: student.uuid,
        score,
        notes,
      });

      toast({
        title: t('interview.success.saved'),
        description: t('interview.success.savedDescription'),
      });

      setIsEditing(false);
      onScoreUpdated?.();
    } catch (error) {
      toast({
        title: t('interview.error.saveFailed'),
        description: error.response?.data?.error || error.message,
        variant: 'destructive',
      });
    }
  };

  const handleCancel = () => {
    setScore(student?.interview_score ?? null);
    setNotes(student?.interview_notes || '');
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Don't render if user can't score
  if (!canScore) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="bg-muted/40 border-b border-border py-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center text-sm font-semibold text-foreground">
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#F28482]/10 mr-3">
              <ClipboardCheck className="h-4 w-4 text-[#F28482]" />
            </span>
            {t('interview.title')}
          </CardTitle>

          {!isEditing && canEdit && !readOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              <Edit className="h-4 w-4 mr-2" />
              {student?.interview_score ? t('interview.edit') : t('interview.addScore')}
            </Button>
          )}

          {isLocked && (
            <Badge variant="outline" className="text-amber-600 border-amber-300">
              <Lock className="h-3 w-3 mr-1" />
              {t('interview.locked')}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="pt-6 space-y-4">
        {isEditing ? (
          <>
            {/* Score Selection */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('interview.score')} *
              </label>
              <div className="flex gap-2">
                {SCORE_OPTIONS.map((option) => (
                  <Button
                    key={option}
                    type="button"
                    variant={score === option ? 'default' : 'outline'}
                    className={`w-12 h-12 text-lg font-bold ${
                      score === option
                        ? 'bg-[#F28482] hover:bg-[#e07270] text-white border-[#F28482]'
                        : 'hover:bg-[#F28482]/10 hover:border-[#F28482]/30'
                    }`}
                    onClick={() => setScore(option)}
                  >
                    {option}
                  </Button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('interview.notes')}
                <span className="text-muted-foreground ml-2">
                  ({notes.length}/{MAX_NOTES_LENGTH})
                </span>
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, MAX_NOTES_LENGTH))}
                placeholder={t('interview.notesPlaceholder')}
                rows={3}
                maxLength={MAX_NOTES_LENGTH}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleSave}
                disabled={score === null || scoreInterviewMutation.isPending}
                className="bg-[#F28482] hover:bg-[#e07270] text-white"
              >
                {scoreInterviewMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {t('interview.save')}
              </Button>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={scoreInterviewMutation.isPending}
              >
                <X className="h-4 w-4 mr-2" />
                {t('interview.cancel')}
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Display Score */}
            {student?.interview_score ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold text-foreground">
                    {student.interview_score}/5
                  </span>
                  <div className="flex gap-1">
                    {SCORE_OPTIONS.map((option) => (
                      <div
                        key={option}
                        className={`w-3 h-3 rounded-full ${
                          option <= student.interview_score
                            ? 'bg-[#F28482]'
                            : 'bg-muted'
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {student.interview_notes && (
                  <p className="text-sm text-foreground bg-muted/50 p-3 rounded-md">
                    "{student.interview_notes}"
                  </p>
                )}

                {/* Scorer Info */}
                {student.interview_scored_by_name && (
                  <div className="text-xs text-muted-foreground flex items-center gap-4 pt-2 border-t">
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {t('interview.scoredBy')}: {student.interview_scored_by_name}
                    </span>
                    {student.interview_scored_at && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(student.interview_scored_at)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground">
                <ClipboardCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>{t('interview.notScored')}</p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default InterviewScoringPanel;
